import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { variableGlobal } from 'src/app/global.enum';

export interface ChequeDTO {
  account: string;
  dueDate: string; // YYYY-MM-DD
  ammount: number;
  bankTransfer: string; // bank code
  endosado: string; // "SI" | "NO"
  checkNum: string;
}

export interface TransferenciaDTO {
  account: string;
  date: string; // YYYY-MM-DD
  reference: string;
  ammount: number;
  cashFlowLineItemID?: number;
}

export interface EfectivoDTO {
  account: string;
  ammount: number;
}

export interface TarjetaCreditoDTO {
  Number: number; // posición/índice visual
  creditCard: number; // OCRC.CreditCard (Int32)
  account: string; // acctCode
  creditCardNumber: string;
  validUntil: string; // YYYY-MM-DD
  ownerIDNumber: string;
  ownerPhone: string;
  paymentMethod: string; // CrTypeCode
  ammountDue: number;
  numerOfPayment: number;
  firsAmmountPayment: number;
  eadAddAmmountPayment: number;
  voucherNumebr: string | number;
  transactionType?: string; // "S" etc
  splitCreditVoucher?: boolean;
}

export interface PagoRecibidoPayload {
  cardCode: string;
  fecha: string; // dd/MM/yyyy (mantener igual a backend .NET)
  comments: string;
  docEntryReference: number | string;
  objTypeReference: string;
  commentsReference: string;
  cheque: ChequeDTO[];
  transferenciaBancaria: TransferenciaDTO | null;
  tarjetaCredito: TarjetaCreditoDTO[];
  efectivo: EfectivoDTO | null;
  origen?: string;
  idUsuario?: string;
}

// Estructura esperada por GetRCT3 (basado en uso del JS legado)
export interface RCT3Entry {
  CreditAcct: string;   // cuenta mayor elegida
  DocNum: number;       // DocEntry ORCT
  AcctCode: string;     // código de tarjeta (OCRC)
  CardValid: string;    // fecha
  CrCardNum: string;
  OwnderIdNum: string;
  OwnerPhone: string;
  CrTypeCode: string;
  NumOfPmnts: number;
  CreditType: string;
  voucherNumebr: string | number;
  CreditSum: number;
  FirstSum: number;
  AddPmntSum: number;
}

@Injectable({ providedIn: 'root' })
export class PagoService {
  constructor(private http: HttpClient) {}

  private rct3Dataset: RCT3Entry[] = [];
  private rct3Loaded = false;

  setRCT3Dataset(list: RCT3Entry[]) {
    this.rct3Dataset = Array.isArray(list) ? list : [];
    this.rct3Loaded = true;
  }

 
  async fetchRCT3DatasetFromAPI(top: string = "", where: string = ""): Promise<RCT3Entry[]> {
    const url = environment.RUTA_API + '/api/INTRANETV1/GetFileQuery';
    const body = {
      token: variableGlobal.TOKEN,
      objeto: { nombre: 'RCT3', top, where }
    };
    const res: any = await this.http.post<any>(url, body).toPromise();
    const lista: any[] = Array.isArray(res?.objeto) ? res.objeto : (Array.isArray(res) ? res : []);
    // No forzamos el shape; asumimos backend entrega campos compatibles con RCT3Entry
    this.setRCT3Dataset(lista as RCT3Entry[]);
    return this.rct3Dataset;
  }

  async generaPagoRecibido(payload: PagoRecibidoPayload) {
    // API (no MVC): requiere envoltorio con token y objeto
    const url = environment.RUTA_API + '/api/INTRANETV1/GeneraPagoDoucmentoRelacionado';
    // Normalizadores de fecha: aceptan dd/MM/yyyy, Date, ISO y devuelven 'YYYY-MM-DD'
    const toISOyyyyMMdd = (d?: string | Date | null): string | undefined => {
      if (!d) return undefined;
      if (d instanceof Date) {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      }
      const s = String(d).trim();
      // dd/MM/yyyy -> yyyy-MM-dd
      const m1 = s.match(/^([0-3]?\d)\/([0-1]?\d)\/(\d{4})$/);
      if (m1) {
        const dd = m1[1].padStart(2, '0');
        const mm = m1[2].padStart(2, '0');
        const yyyy = m1[3];
        return `${yyyy}-${mm}-${dd}`;
      }
      // yyyy-MM-dd or ISO -> slice
      const dt = new Date(s);
      if (!isNaN(dt.getTime())) {
        return dt.toISOString().slice(0, 10);
      }
      // fallback: return as-is (backend podría rechazar)
      return s;
    };

    const tarjetaCredito = (payload.tarjetaCredito || []).map(tc => {
      const normalized = toISOyyyyMMdd(tc.validUntil);
      // añadimos también la propiedad en PascalCase para máxima compatibilidad
      return {
        ...tc,
        validUntil: normalized ?? tc.validUntil,
        ValidUntil: (normalized ?? tc.validUntil) as any,
      } as any;
    });

    // Normalizar fecha raíz
    const fechaISO = toISOyyyyMMdd(payload.fecha);

    // Normalizar fechas en cheques y transferencias
    const cheques = (payload.cheque || []).map(ch => ({
      ...ch,
      dueDate: toISOyyyyMMdd(ch.dueDate) ?? ch.dueDate,
    }));

    // Transferencia: si viene vacía, forzar a null
    const t = payload.transferenciaBancaria;
    const hasT = !!t && (
      (t.account && t.account.trim() !== '') ||
      (t.date && String(t.date).trim() !== '') ||
      (t.reference && t.reference.trim() !== '') ||
      (Number(t.ammount) > 0)
    );
    const transferencia = hasT
      ? { ...t, date: toISOyyyyMMdd(t.date) ?? t.date }
      : null;

    // Efectivo: si viene vacío o 0, forzar a null
    const e = payload.efectivo;
    const hasE = !!e && (
      (e.account && e.account.trim() !== '') ||
      (Number(e.ammount) > 0)
    );
    const efectivo = hasE ? e : null;

  const objeto = { ...payload, fecha: fechaISO ?? payload.fecha, cheque: cheques, transferenciaBancaria: transferencia, tarjetaCredito, efectivo, idUsuario: localStorage.getItem('RUT_LOGUEADO') || '', origen: payload.origen || 'Venta / Seguimiento' };
    const body = {
      token: variableGlobal.TOKEN,
      objeto
    };
    
    return this.http.post<any>(url, body).toPromise();
  }

}

